import React, { useState, useEffect } from "react";
import FieldDropdown from "../common/InputField/FieldDropdown";
import StepBackButton from "../common/StepBackButton/StepBackButton";
import Popup from "../popup/Popup";
import { POPUP_FORM_CONTENT_CLASS, ACTION_POPUP_CONTENT_INNER_CLASS } from "../popup/popupLayoutSpec";
import { runTextPipeline } from "../../utils/contentSafety";
import {
  AdminPageTitle,
  AdminMainText,
  AdminSupportingText,
  AdminDetailFieldLabel,
  AdminHeaderAction,
  AdminFormFieldError,
  adminWizardDropdownClasses,
  adminWizardTextareaClasses,
} from "../../sections/AdminDashboard/shared/adminUx";

const MUTED_DROPDOWN_PANEL = "custom-dropdown-panel !bg-mistygray";

const CancelSubscriptionPopup = ({ isOpen, onClose, subscriptionData }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [commentsError, setCommentsError] = useState("");
  const [formData, setFormData] = useState({
    cancellationType: "end_of_period",
    reason: "",
    comments: "",
  });

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setFormData({
        cancellationType: "end_of_period",
        reason: "",
        comments: "",
      });
      setCommentsError("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (currentStep === 4) {
      const timer = setTimeout(() => onClose(), 5000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, onClose]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      setCurrentStep(formData.reason === "Other" ? 3 : 4);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleCancelSubscription = () => {
    setCommentsError("");
    if (formData.comments && String(formData.comments).trim()) {
      const result = runTextPipeline(formData.comments.trim(), "support");
      if (!result.valid) {
        setCommentsError(result.message || "Please use clear, appropriate text.");
        return;
      }
    }
    setCurrentStep(4);
  };

  const reasonOptions = [
    "Too expensive",
    "Not enough leads",
    "Seasonal break",
    "Switching platform",
    "Business closed",
    "Other",
  ];

  const cancelOptionClass = (selected) =>
    `w-full h-[48px] rounded p-3 cursor-pointer transition-all duration-200 flex items-center ${
      selected ? "bg-lightgray" : "bg-mistygray hover:bg-lightgray"
    }`;

  if (!isOpen) return null;

  return (
    <Popup
      setTrigger={() => {}}
      ShowCrossButton={true}
      cross={true}
      onClose={onClose}
      businessDashboard={true}
      hideLogoInHeader={true}
    >
      <div
        className="overflow-auto relative flex items-center justify-center"
        style={{ minHeight: "calc(100vh - 120px)" }}
      >
        <div className={POPUP_FORM_CONTENT_CLASS}>
          <div className={ACTION_POPUP_CONTENT_INNER_CLASS}>
            {currentStep === 1 ? (
              <div>
                <AdminPageTitle className="text-center pb-0 text-offblack mb-6">
                  Cancel your subscription
                </AdminPageTitle>

                <div className="bg-mistygray rounded-lg p-4 mb-6 space-y-2">
                  <div className="flex justify-between gap-4">
                    <AdminMainText className="text-offblack">Plan:</AdminMainText>
                    <AdminMainText className="text-offblack">{subscriptionData?.plan || "Basic Plan"}</AdminMainText>
                  </div>
                  <div className="flex justify-between gap-4">
                    <AdminMainText className="text-offblack">Next renewal:</AdminMainText>
                    <AdminMainText className="text-offblack">
                      {subscriptionData?.renewsOn || "25 July 2025"}
                    </AdminMainText>
                  </div>
                  <div className="flex justify-between gap-4">
                    <AdminMainText className="text-offblack">Price:</AdminMainText>
                    <AdminMainText className="text-offblack">
                      {subscriptionData?.nextPayment || "$49.95"}
                    </AdminMainText>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <button
                    type="button"
                    className={cancelOptionClass(formData.cancellationType === "end_of_period")}
                    onClick={() => handleInputChange("cancellationType", "end_of_period")}
                  >
                    <AdminMainText className="text-offblack">
                      End at the end of billing period (recommended)
                    </AdminMainText>
                  </button>
                  <button
                    type="button"
                    className={cancelOptionClass(formData.cancellationType === "immediate")}
                    onClick={() => handleInputChange("cancellationType", "immediate")}
                  >
                    <AdminMainText className="text-offblack">
                      End immediately (your plan features will stop today)
                    </AdminMainText>
                  </button>
                </div>

                <div className="flex justify-end">
                  <AdminHeaderAction type="button" onClick={handleNext}>
                    Next
                  </AdminHeaderAction>
                </div>
              </div>
            ) : null}

            {currentStep === 2 ? (
              <div>
                <AdminDetailFieldLabel className="mb-3 block">Reason for cancelling</AdminDetailFieldLabel>
                <FieldDropdown
                  surface="muted"
                  fullWidth
                  value={formData.reason}
                  onChange={(e) => handleInputChange("reason", e.value)}
                  options={reasonOptions}
                  placeholder="Select a reason"
                  className={adminWizardDropdownClasses(false, true)}
                  panelClassName={MUTED_DROPDOWN_PANEL}
                  appendTo={typeof document !== "undefined" ? document.body : null}
                />

                <div className="flex items-center justify-between mt-6">
                  <StepBackButton onClick={handlePrevious} />
                  <AdminHeaderAction type="button" onClick={handleNext} disabled={!formData.reason}>
                    Next
                  </AdminHeaderAction>
                </div>
              </div>
            ) : null}

            {currentStep === 3 ? (
              <div>
                <div className="space-y-1 w-full min-w-0 mb-6">
                  <AdminDetailFieldLabel htmlFor="cancel-comments">Additional comments</AdminDetailFieldLabel>
                  <textarea
                    id="cancel-comments"
                    placeholder="e.g. 'Leads were slow in my area...'"
                    value={formData.comments}
                    onChange={(e) => {
                      handleInputChange("comments", e.target.value);
                      setCommentsError("");
                    }}
                    className={`${adminWizardTextareaClasses(!!commentsError)} min-h-[120px]`}
                    style={{ resize: "none" }}
                  />
                  {commentsError ? <AdminFormFieldError>{commentsError}</AdminFormFieldError> : null}
                </div>

                <div className="flex items-center justify-between">
                  <StepBackButton onClick={handlePrevious} />
                  <AdminHeaderAction type="button" onClick={handleCancelSubscription}>
                    Next
                  </AdminHeaderAction>
                </div>
              </div>
            ) : null}

            {currentStep === 4 ? (
              <div className="text-center">
                {formData.cancellationType === "end_of_period" ? (
                  <>
                    <AdminPageTitle className="text-center pb-0 text-offblack mb-2">
                      Cancellation scheduled
                    </AdminPageTitle>
                    <AdminMainText className="text-offblack mb-4 block">
                      Your subscription will remain active until{" "}
                      {subscriptionData?.renewsOn || "25 July 2025"}. You won&apos;t be charged again.
                    </AdminMainText>
                  </>
                ) : (
                  <>
                    <AdminPageTitle className="text-center pb-0 text-offblack mb-2">
                      Subscription cancelled
                    </AdminPageTitle>
                    <AdminMainText className="text-offblack block">
                      Your subscription has ended today. You won&apos;t be charged again.
                    </AdminMainText>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default CancelSubscriptionPopup;
